import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { isEvening } from '../lib/floor';
import { setSessionBusiness, staffSession } from '../lib/owner';
import { ACCENTS, AWNINGS, EXTRAS, SIGN_COLORS, SIGN_MAX, SIGNS, STYLES, SUB_MAX, WALLS, type FrontExtra, type Storefront as Front } from '../lib/storefront';
import { BUSINESS, defaultProfile, DELIVERY_MAX_M, MARKET_NAME_MAX, townProfileErrors, withType, type BusinessType, type TownProfile } from '../lib/townProfile';
import { Shell } from './Shell';
import { useStaff } from './useStaff';

const Preview = lazy(() => import('../town/StorefrontPreview'));
const DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
/** A first storefront that fits what the owner said they run when they signed up. */
const starting = (name: string) => { const b = staffSession()?.business; const p = defaultProfile(name); return b && b !== 'shop' ? withType(p, b) : p; };

/**
 * /storefront — owners: how the restaurant appears in FirstMango Town. Business type, the storefront look (live 3D
 * preview on a short street), market days for market stalls, location, delivery radius and the town listing.
 */
export function Storefront({ path }: { path: string }) {
  const { s, online } = useStaff(() => undefined, () => undefined);
  const owner = staffSession()?.role === 'owner';
  const name = s?.name ?? 'ร้านของฉัน';
  const [p, setP] = useState<TownProfile | null>(null);
  const [saved, setSaved] = useState<string>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [noGl, setNoGl] = useState(false);
  // Coordinates as typed (a half-typed pair must not wipe the other field).
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const typed = (a: string, b: string) => { setLat(a); setLng(b); set({ location: coord(a, b) }); };
  const onFail = useCallback(() => setNoGl(true), []);

  useEffect(() => {
    if (!s || !owner) return;
    api<{ profile: TownProfile | null }>('GET', '/restaurant/town', { token: s.token })
      .then((r) => { const v = r.profile ?? starting(name); setP(v); setSaved(r.profile ? JSON.stringify(r.profile) : ''); setLat(v.location ? String(v.location.lat) : ''); setLng(v.location ? String(v.location.lng) : ''); })
      .catch(() => setP(starting(name)));
  }, [s, owner, name]);

  const errors = useMemo(() => (p ? townProfileErrors(p) : []), [p]);
  const dirty = !!p && JSON.stringify(p) !== saved;
  const set = (patch: Partial<TownProfile>) => setP((v) => (v ? { ...v, ...patch } : v));
  const setFront = (patch: Partial<Front>) => setP((v) => (v ? { ...v, front: { ...v.front, ...patch } } : v));
  const setSign = (patch: Partial<Front['sign']>) => setP((v) => (v ? { ...v, front: { ...v.front, sign: { ...v.front.sign, ...patch } } } : v));

  const save = async () => {
    if (!s || !p || errors.length) return;
    setBusy(true);
    try {
      const r = await api<{ profile: TownProfile }>('PUT', '/restaurant/town', { token: s.token, body: { profile: p } });
      setP(r.profile);
      setSaved(JSON.stringify(r.profile));
      setSessionBusiness(r.profile.type);
      setMsg({ ok: true, text: r.profile.listed ? 'บันทึกแล้ว ร้านของคุณอยู่ในเมือง · Saved. Your shop is in the town.' : 'บันทึกแล้ว · Saved (not shown in the town yet).' });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'บันทึกไม่ได้ ลองอีกครั้ง · Could not save. Try again.' });
    } finally { setBusy(false); }
  };

  const locate = () => {
    if (!navigator.geolocation) { setMsg({ ok: false, text: 'เครื่องนี้หาตำแหน่งไม่ได้ · This device cannot find its location.' }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => typed(String(Math.round(pos.coords.latitude * 1e6) / 1e6), String(Math.round(pos.coords.longitude * 1e6) / 1e6)),
      () => setMsg({ ok: false, text: 'ไม่ได้รับอนุญาตให้ใช้ตำแหน่ง ใส่พิกัดเองได้ · Location not allowed; you can type the coordinates.' }),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  if (!owner) return <Shell path="/settings" online={online} sub="storefront"><div className="panel"><p style={{ margin: 0 }}>เฉพาะเจ้าของร้าน · Only the owner can change the storefront.</p></div><span hidden>{path}</span></Shell>;
  const f = p?.front;
  const styles = p ? BUSINESS[p.type].styles : [];
  return (
    <Shell path="/settings" online={online} sub="หน้าร้าน · Storefront">
      {p && f && (
        <div className="sf-wrap">
          <div className="sf-left">
            {noGl ? <div className="sf-stage sf-nogl">เครื่องนี้แสดง 3D ไม่ได้ · This device can't draw the 3D preview.</div> : (
              <Suspense fallback={<div className="sf-stage"><div className="spinner" role="status" aria-label="Loading preview" style={{ margin: 'auto' }} /></div>}>
                <Preview profile={p} name={name} evening={isEvening(Date.now())} onFail={onFail} />
              </Suspense>
            )}
            <p className="sf-hint">ลากเพื่อหมุน · เลื่อนเพื่อซูม · Drag to turn, scroll to zoom</p>
          </div>

          <div className="sf-panel">
            <section className="sf-sec">
              <h2>ประเภทร้าน · Business type</h2>
              <div className="sf-grid2">
                {(Object.keys(BUSINESS) as BusinessType[]).map((t) => (
                  <button key={t} className={`sf-type${p.type === t ? ' on' : ''}`} aria-pressed={p.type === t} onClick={() => setP(withType(p, t))}>
                    <b>{BUSINESS[t].th} · {BUSINESS[t].en}</b><span>{BUSINESS[t].hint}</span>
                  </button>
                ))}
              </div>
            </section>

            {p.type === 'market' && p.market && (
              <section className="sf-sec">
                <h2>ตลาด · Market</h2>
                <label className="sf-field">ชื่อตลาด · Market name (stalls with the same name stand together)
                  <input value={p.market.name} maxLength={MARKET_NAME_MAX * 2} onChange={(e) => set({ market: { ...p.market!, name: e.target.value } })} placeholder="ตลาดนัดริมคลอง" />
                </label>
                <div className="sf-chips" role="group" aria-label="Market days">
                  {DAYS.map((d, i) => {
                    const on = p.market!.hours.days.includes(i);
                    return <button key={d} className={on ? 'on' : ''} aria-pressed={on} onClick={() => set({ market: { ...p.market!, hours: { ...p.market!.hours, days: on ? p.market!.hours.days.filter((x) => x !== i) : [...p.market!.hours.days, i].sort() } } })}>{d}</button>;
                  })}
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <label className="sf-field" style={{ flex: 1 }}>เปิด · Opens<input type="time" value={p.market.hours.open} onChange={(e) => set({ market: { ...p.market!, hours: { ...p.market!.hours, open: e.target.value } } })} /></label>
                  <label className="sf-field" style={{ flex: 1 }}>ปิด · Closes<input type="time" value={p.market.hours.close} onChange={(e) => set({ market: { ...p.market!, hours: { ...p.market!.hours, close: e.target.value } } })} /></label>
                </div>
              </section>
            )}

            <section className="sf-sec">
              <h2>หน้าตาร้าน · Look</h2>
              {styles.length > 1 && (
                <div className="sf-chips" role="group" aria-label="Style">
                  {styles.map((st) => <button key={st} className={f.style === st ? 'on' : ''} aria-pressed={f.style === st} onClick={() => setFront({ style: st })}>{STYLES[st].th} · {STYLES[st].en}</button>)}
                </div>
              )}
              {p.type === 'shop' && (
                <div className="sf-chips" role="group" aria-label="Width">
                  {([1, 2, 3] as const).map((n) => <button key={n} className={f.lots === n ? 'on' : ''} aria-pressed={f.lots === n} onClick={() => setFront({ lots: n })}>{n} คูหา · {n * 4} m</button>)}
                </div>
              )}
              <div className="sf-label">สีผนัง · Walls</div>
              <Swatches colors={WALLS} value={f.wall} onPick={(c) => setFront({ wall: c })} />
              <div className="sf-label">สีหลัก · Accent (awning, frames, tent)</div>
              <Swatches colors={ACCENTS} value={f.accent} onPick={(c) => setFront({ accent: c })} />
              {p.type === 'shop' && (
                <div className="sf-chips" role="group" aria-label="Awning">
                  {(Object.keys(AWNINGS) as (keyof typeof AWNINGS)[]).map((a) => <button key={a} className={f.awning === a ? 'on' : ''} aria-pressed={f.awning === a} onClick={() => setFront({ awning: a })}>กันสาด {AWNINGS[a].th} · {AWNINGS[a].en}</button>)}
                </div>
              )}
              <div className="sf-label">ของตกแต่ง · Extras</div>
              <div className="sf-chips" role="group" aria-label="Extras">
                {(Object.keys(EXTRAS) as FrontExtra[]).map((x) => {
                  const on = f.extras.includes(x);
                  return <button key={x} className={on ? 'on' : ''} aria-pressed={on} onClick={() => setFront({ extras: on ? f.extras.filter((y) => y !== x) : [...f.extras, x] })}>{EXTRAS[x].th} · {EXTRAS[x].en}</button>;
                })}
              </div>
            </section>

            <section className="sf-sec">
              <h2>ป้าย · Sign</h2>
              <label className="sf-field">ชื่อบนป้าย · Sign text<input value={f.sign.text} maxLength={SIGN_MAX * 2} onChange={(e) => setSign({ text: e.target.value })} /></label>
              <label className="sf-field">บรรทัดล่าง · Second line (optional)<input value={f.sign.sub ?? ''} maxLength={SUB_MAX * 2} onChange={(e) => setSign({ sub: e.target.value || undefined })} placeholder="Thai kitchen · since 1998" /></label>
              <div className="sf-chips" role="group" aria-label="Sign style">
                {(Object.keys(SIGNS) as (keyof typeof SIGNS)[]).map((x) => <button key={x} className={f.sign.style === x ? 'on' : ''} aria-pressed={f.sign.style === x} onClick={() => setSign({ style: x })}>{SIGNS[x].th} · {SIGNS[x].en}</button>)}
              </div>
              <Swatches colors={SIGN_COLORS} value={f.sign.color} onPick={(c) => setSign({ color: c })} />
            </section>

            <section className="sf-sec">
              <h2>ที่ตั้งและการส่ง · Location & delivery</h2>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button className="btn-dark" onClick={locate}>📍 ใช้ตำแหน่งตอนนี้ · Use where I am</button>
                {p.location && <a className="sf-link" href={`https://www.google.com/maps?q=${p.location.lat},${p.location.lng}`} target="_blank" rel="noreferrer">ดูบนแผนที่ · Check on the map ↗</a>}
              </div>
              <div className="row" style={{ gap: 8 }}>
                <label className="sf-field" style={{ flex: 1 }}>Lat<input inputMode="decimal" value={lat} onChange={(e) => typed(e.target.value, lng)} placeholder="13.7563" /></label>
                <label className="sf-field" style={{ flex: 1 }}>Lng<input inputMode="decimal" value={lng} onChange={(e) => typed(lat, e.target.value)} placeholder="100.5018" /></label>
              </div>
              <label className="sf-field">ส่งถึงบ้าน · Own delivery: {p.deliveryM ? `${(p.deliveryM / 1000).toFixed(1)} กม. · km` : 'ไม่ส่ง · none'}
                <input type="range" min={0} max={DELIVERY_MAX_M} step={500} value={p.deliveryM} onChange={(e) => set({ deliveryM: Number(e.target.value) })} />
              </label>
              <label className="sf-switch">
                <input type="checkbox" checked={p.listed && !!p.location} disabled={!p.location} onChange={(e) => set({ listed: e.target.checked })} />
                <span>แสดงร้านในเมืองเฟิร์สแมงโก้ · Show my shop in FirstMango Town{!p.location ? ' (ต้องมีที่ตั้ง · needs a location)' : ''}</span>
              </label>
              <p className="sf-note">ลูกค้าเห็นแค่ระยะทาง ไม่เห็นพิกัดของคุณ และตำแหน่งของลูกค้าไม่ถูกเก็บ · Visitors see how far you are, not your coordinates; their location is never stored.</p>
            </section>

            {errors.length > 0 && <div className="warn" role="alert" style={{ color: 'var(--ink)' }}>{errors.map((e) => <div key={e}>{e}</div>)}</div>}
            {msg && <div className={msg.ok ? 'ticket' : 'warn'} role="status" style={{ color: 'var(--ink)' }}>{msg.text}</div>}
            <button className="cta light sf-save" disabled={busy || !dirty || errors.length > 0} onClick={() => void save()}>
              {busy ? 'กำลังบันทึก… · Saving…' : dirty ? 'บันทึก · Save' : 'บันทึกแล้ว · Saved'}
            </button>
          </div>
        </div>
      )}
      <span hidden>{path}</span>
    </Shell>
  );
}

function coord(lat: string, lng: string): TownProfile['location'] {
  const a = Number(lat), b = Number(lng);
  return !lat.trim() || !lng.trim() || !Number.isFinite(a) || !Number.isFinite(b) ? null : { lat: a, lng: b };
}

function Swatches({ colors, value, onPick }: { colors: string[]; value: string; onPick: (c: string) => void }) {
  return (
    <div className="sf-swatches" role="group">
      {colors.map((c) => <button key={c} className={value.toUpperCase() === c.toUpperCase() ? 'on' : ''} style={{ background: c }} aria-label={c} aria-pressed={value.toUpperCase() === c.toUpperCase()} onClick={() => onPick(c)} />)}
    </div>
  );
}
